import {
  CalculatorClaimDateContractError,
  parseCalculatorClaimDateV1,
} from "./contracts/calculator-claim-date-v1.js";

const FACT_ID_PATTERN = /^blake3:[0-9a-f]{64}$/;
const HASH_DIGEST_PATTERN = /^[0-9a-f]{64}$/;
export const U8_MAX = 255;
export const U16_MAX = 65_535;
export const U32_MAX = 4_294_967_295;
export const MAX_CALCULATOR_OVERLAY_PRESET_OPERATIONS_V4 = 10_000;
export const MAX_CALCULATOR_OVERLAY_PRESET_NESTED_LIST_ITEMS_V4 = 128;
export const MAX_CALCULATOR_OVERLAY_PRESET_STRING_CHARS_V4 = 512;
export const MAX_CALCULATOR_OVERLAY_PRESET_COMMENT_CHARS_V4 = 2_000;
export const MAX_CALCULATOR_OVERLAY_PRESET_PAYLOAD_BYTES_V4 = 256 * 1_024;
export const MAX_CALCULATOR_OVERLAY_PRESET_JSON_SAFE_INTEGER_V4 = Number.MAX_SAFE_INTEGER;

const RATE_BASES = new Set([
  "ratio",
  "percent",
  "denormalizedWeight",
  "rawWeight",
  "perCatch",
  "perHour",
]);
const NUMERIC_UNITS = new Set([
  "seconds",
  "count",
  "perHour",
  "perDay",
  "silver",
  "experience",
  "percent",
  "ratio",
  "multiplier",
  "durability",
  "days",
]);
const UNAVAILABLE_REASON_CODES = new Set([
  "missingSourceFact",
  "unsupportedSourceVariant",
  "noEquivalentRawWeight",
  "incompleteProvenance",
  "notApplicable",
  "modelSelectionRequired",
]);
const OPERATION_CONFIDENCE_VALUES = new Set(["known", "guessed"]);
const CANONICAL_UTC_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})Z$/;

export function isRecord(value) {
  return Boolean(value)
    && typeof value === "object"
    && !Array.isArray(value)
    && Object.prototype.toString.call(value) === "[object Object]";
}

export function fieldPath(path, field) {
  return `${path}.${field}`;
}

export function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return Object.freeze(value);
}

export class CalculatorUserLayerV4ValidationError extends Error {
  constructor(issues, message = "Calculator user-layer v4 validation failed") {
    const normalizedIssues = Array.isArray(issues) ? issues : [];
    const detail = normalizedIssues
      .map((issue) => `${issue.path}: ${issue.message}`)
      .join("; ");
    super(detail ? `${message}: ${detail}` : message);
    this.name = "CalculatorUserLayerV4ValidationError";
    this.issues = deepFreeze(normalizedIssues.map((issue) => ({ ...issue })));
  }
}

export class ValidationContext {
  constructor() {
    this.issues = [];
  }

  issue(path, code, message) {
    this.issues.push({ path, code, message });
  }

  exactObject(value, path, allowedFields) {
    if (!isRecord(value)) {
      this.issue(path, "invalidType", "expected an object");
      return {};
    }
    const allowed = new Set(allowedFields);
    for (const key of Object.keys(value)) {
      if (!allowed.has(key)) {
        this.issue(fieldPath(path, key), "unknownField", "unknown field");
      }
    }
    return value;
  }

  string(value, path, options = {}) {
    if (typeof value !== "string") {
      this.issue(path, "invalidType", "expected a string");
      return "";
    }
    if (!value.trim()) {
      this.issue(path, "emptyString", "must not be empty");
      return value;
    }
    if (options.opaqueId === true && value !== value.trim()) {
      this.issue(path, "invalidId", "must not contain surrounding whitespace");
    }
    return value;
  }

  optionalString(value, path, options = {}) {
    if (value === undefined || value === null) {
      return null;
    }
    return this.string(value, path, options);
  }

  enum(value, path, values) {
    if (typeof value !== "string" || !values.has(value)) {
      this.issue(
        path,
        "unsupportedValue",
        `expected one of: ${Array.from(values).join(", ")}`,
      );
      return "";
    }
    return value;
  }

  integer(value, path, minimum, maximum) {
    if (!Number.isInteger(value) || value < minimum || value > maximum) {
      this.issue(
        path,
        "invalidInteger",
        `expected an integer from ${minimum} through ${maximum}`,
      );
      return minimum;
    }
    return value;
  }

  finiteNumber(value, path, nonNegative = false) {
    if (
      typeof value !== "number"
      || !Number.isFinite(value)
      || (nonNegative && value < 0)
    ) {
      this.issue(
        path,
        "invalidNumber",
        nonNegative
          ? "expected a finite non-negative number"
          : "expected a finite number",
      );
      return 0;
    }
    return Object.is(value, -0) ? 0 : value;
  }

  array(value, path, { maxItems = MAX_CALCULATOR_OVERLAY_PRESET_NESTED_LIST_ITEMS_V4 } = {}) {
    if (!Array.isArray(value)) {
      this.issue(path, "invalidType", "expected an array");
      return [];
    }
    if (value.length > maxItems) {
      this.issue(path, "invalidLength", `expected at most ${maxItems} items`);
      return value.slice(0, maxItems);
    }
    return value;
  }

  factId(value, path) {
    if (typeof value !== "string" || !FACT_ID_PATTERN.test(value)) {
      this.issue(
        path,
        "invalidFactId",
        "expected blake3:<64 lowercase hexadecimal characters>",
      );
      return "";
    }
    return value;
  }

  finish() {
    if (this.issues.length) {
      throw new CalculatorUserLayerV4ValidationError(this.issues);
    }
  }
}

function parseContentHash(value, path, context) {
  const object = context.exactObject(value, path, ["algorithm", "digest"]);
  const algorithm = context.enum(
    object.algorithm,
    fieldPath(path, "algorithm"),
    new Set(["blake3", "sha256"]),
  );
  const digest = context.string(object.digest, fieldPath(path, "digest"));
  if (digest && !HASH_DIGEST_PATTERN.test(digest)) {
    context.issue(
      fieldPath(path, "digest"),
      "invalidHashDigest",
      "expected 64 lowercase hexadecimal characters",
    );
  }
  return { algorithm, digest };
}

function parseOptionalContentHash(value, path, context) {
  if (value === undefined || value === null) {
    return null;
  }
  return parseContentHash(value, path, context);
}

function parseOperationAuthor(value, path, context) {
  const object = context.exactObject(value, path, [
    "id",
    "displayName",
    "handle",
  ]);
  const text = (field, { opaqueId = false } = {}) => {
    const fieldValue = context.string(
      object[field],
      fieldPath(path, field),
      { opaqueId },
    );
    if (!opaqueId && fieldValue !== fieldValue.trim()) {
      context.issue(
        fieldPath(path, field),
        "invalidValue",
        "must not contain surrounding whitespace",
      );
    }
    return fieldValue;
  };
  return {
    id: text("id", { opaqueId: true }),
    displayName: text("displayName"),
    handle: text("handle"),
  };
}

export function isCanonicalCalculatorCreatedAtUtc(value) {
  if (typeof value !== "string") {
    return false;
  }
  const match = CANONICAL_UTC_PATTERN.exec(value);
  if (!match) {
    return false;
  }
  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    0,
    31,
    leapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month] ?? 0;
  return !(
    day < 1
    || day > daysInMonth
    || hour > 23
    || minute > 59
    || second > 59
  );
}

function parseCanonicalCreatedAtUtc(value, path, context) {
  const timestamp = context.string(value, path);
  if (!isCanonicalCalculatorCreatedAtUtc(timestamp)) {
    context.issue(
      path,
      "invalidTimestamp",
      "expected a valid canonical YYYY-MM-DDTHH:mm:ss.sssZ timestamp",
    );
  }
  return timestamp;
}

export function parseLayerProvenance(value, path, context) {
  const object = context.exactObject(value, path, [
    "sourceId",
    "sourceRevision",
    "contentHash",
    "importedAtUtc",
  ]);
  return {
    sourceId: context.string(object.sourceId, fieldPath(path, "sourceId"), {
      opaqueId: true,
    }),
    sourceRevision: context.optionalString(
      object.sourceRevision,
      fieldPath(path, "sourceRevision"),
    ),
    contentHash: parseContentHash(
      object.contentHash,
      fieldPath(path, "contentHash"),
      context,
    ),
    importedAtUtc: context.optionalString(
      object.importedAtUtc,
      fieldPath(path, "importedAtUtc"),
    ),
  };
}

export function parseOperationProvenance(value, path, context) {
  const object = context.exactObject(value, path, [
    "sourceId",
    "comment",
    "confidence",
    "author",
    "createdAtUtc",
    "claimDate",
    "sourceName",
    "sourceRevision",
    "sourceTable",
    "sourceSheet",
    "sourceRow",
    "contentHash",
    "importedAtUtc",
    "observedAtUtc",
    "observationScope",
    "observationCount",
  ]);
  const sourceRow = object.sourceRow === undefined || object.sourceRow === null
    ? null
    : context.integer(
      object.sourceRow,
      fieldPath(path, "sourceRow"),
      0,
      MAX_CALCULATOR_OVERLAY_PRESET_JSON_SAFE_INTEGER_V4,
    );
  const observationScope = object.observationScope === undefined
    || object.observationScope === null
    ? null
    : context.enum(
      object.observationScope,
      fieldPath(path, "observationScope"),
      new Set(["rankingFullyContained", "rankingPartialIntersection"]),
    );
  const observationCount = object.observationCount === undefined
    || object.observationCount === null
    ? null
    : context.integer(
      object.observationCount,
      fieldPath(path, "observationCount"),
      1,
      U32_MAX,
    );
  if ((observationScope === null) !== (observationCount === null)) {
    context.issue(
      path,
      "invalidValue",
      "observationScope and observationCount must either both be null or both be present",
    );
  }
  const comment = context.optionalString(
    object.comment,
    fieldPath(path, "comment"),
  );
  if (comment !== null && comment !== comment.trim()) {
    context.issue(
      fieldPath(path, "comment"),
      "invalidValue",
      "operation comment must not contain surrounding whitespace",
    );
  }
  if (
    comment !== null
    && [...comment].length > MAX_CALCULATOR_OVERLAY_PRESET_COMMENT_CHARS_V4
  ) {
    context.issue(
      fieldPath(path, "comment"),
      "unsupportedValue",
      `operation comment must contain at most ${MAX_CALCULATOR_OVERLAY_PRESET_COMMENT_CHARS_V4} characters`,
    );
  }
  const confidence = object.confidence === undefined || object.confidence === null
    ? null
    : context.enum(
      object.confidence,
      fieldPath(path, "confidence"),
      OPERATION_CONFIDENCE_VALUES,
    );
  const author = object.author === undefined || object.author === null
    ? null
    : parseOperationAuthor(object.author, fieldPath(path, "author"), context);
  const createdAtUtc = object.createdAtUtc === undefined
    || object.createdAtUtc === null
    ? null
    : parseCanonicalCreatedAtUtc(
      object.createdAtUtc,
      fieldPath(path, "createdAtUtc"),
      context,
    );
  if ((author === null) !== (createdAtUtc === null)) {
    context.issue(
      path,
      "invalidValue",
      "author and createdAtUtc must either both be absent or both be present",
    );
  }
  let claimDate = null;
  if (object.claimDate !== undefined && object.claimDate !== null) {
    try {
      claimDate = parseCalculatorClaimDateV1(
        object.claimDate,
        fieldPath(path, "claimDate"),
      );
    } catch (error) {
      if (!(error instanceof CalculatorClaimDateContractError)) {
        throw error;
      }
      context.issue(error.path, "invalidValue", error.message);
    }
  }
  return {
    sourceId: context.string(object.sourceId, fieldPath(path, "sourceId"), {
      opaqueId: true,
    }),
    ...(comment === null ? {} : { comment }),
    ...(confidence === null ? {} : { confidence }),
    ...(author === null ? {} : { author }),
    ...(createdAtUtc === null ? {} : { createdAtUtc }),
    ...(claimDate === null ? {} : { claimDate }),
    sourceName: context.optionalString(
      object.sourceName,
      fieldPath(path, "sourceName"),
    ),
    sourceRevision: context.optionalString(
      object.sourceRevision,
      fieldPath(path, "sourceRevision"),
    ),
    sourceTable: context.optionalString(
      object.sourceTable,
      fieldPath(path, "sourceTable"),
    ),
    sourceSheet: context.optionalString(
      object.sourceSheet,
      fieldPath(path, "sourceSheet"),
    ),
    sourceRow,
    contentHash: parseOptionalContentHash(
      object.contentHash,
      fieldPath(path, "contentHash"),
      context,
    ),
    importedAtUtc: context.optionalString(
      object.importedAtUtc,
      fieldPath(path, "importedAtUtc"),
    ),
    observedAtUtc: context.optionalString(
      object.observedAtUtc,
      fieldPath(path, "observedAtUtc"),
    ),
    observationScope,
    observationCount,
  };
}

function parseUnavailableReason(value, path, context) {
  const object = context.exactObject(value, path, [
    "code",
    "sourceId",
    "detailCode",
  ]);
  return {
    code: context.enum(
      object.code,
      fieldPath(path, "code"),
      UNAVAILABLE_REASON_CODES,
    ),
    sourceId: context.optionalString(object.sourceId, fieldPath(path, "sourceId"), {
      opaqueId: true,
    }),
    detailCode: context.optionalString(
      object.detailCode,
      fieldPath(path, "detailCode"),
      { opaqueId: true },
    ),
  };
}

export function parseRate(value, path, context) {
  const discriminator = isRecord(value) ? value.state : undefined;
  if (discriminator === "known") {
    const object = context.exactObject(value, path, ["state", "value", "basis"]);
    return {
      state: "known",
      value: context.finiteNumber(object.value, fieldPath(path, "value"), true),
      basis: context.enum(object.basis, fieldPath(path, "basis"), RATE_BASES),
    };
  }
  if (discriminator === "unknown") {
    context.exactObject(value, path, ["state"]);
    return { state: "unknown" };
  }
  if (discriminator === "unavailable") {
    const object = context.exactObject(value, path, ["state", "reason"]);
    return {
      state: "unavailable",
      reason: parseUnavailableReason(
        object.reason,
        fieldPath(path, "reason"),
        context,
      ),
    };
  }
  context.exactObject(value, path, ["state"]);
  context.issue(
    fieldPath(path, "state"),
    "unsupportedValue",
    "expected known, unknown, or unavailable",
  );
  return { state: "unknown" };
}

export function parseItemQuantity(value, path, context) {
  const discriminator = isRecord(value) ? value.state : undefined;
  if (discriminator === "known") {
    const object = context.exactObject(value, path, [
      "state",
      "minimum",
      "maximum",
    ]);
    const minimum = context.integer(
      object.minimum,
      fieldPath(path, "minimum"),
      0,
      U32_MAX,
    );
    const maximum = context.integer(
      object.maximum,
      fieldPath(path, "maximum"),
      0,
      U32_MAX,
    );
    if (maximum < minimum) {
      context.issue(
        fieldPath(path, "maximum"),
        "invalidRange",
        "must be at least the minimum",
      );
    }
    return { state: "known", minimum, maximum };
  }
  if (discriminator === "unknown") {
    context.exactObject(value, path, ["state"]);
    return { state: "unknown" };
  }
  if (discriminator === "unavailable") {
    const object = context.exactObject(value, path, ["state", "reason"]);
    return {
      state: "unavailable",
      reason: parseUnavailableReason(
        object.reason,
        fieldPath(path, "reason"),
        context,
      ),
    };
  }
  context.exactObject(value, path, ["state"]);
  context.issue(
    fieldPath(path, "state"),
    "unsupportedValue",
    "expected known, unknown, or unavailable",
  );
  return { state: "unknown" };
}

export function parseNumericValue(value, path, context) {
  const discriminator = isRecord(value) ? value.state : undefined;
  if (discriminator === "known") {
    const object = context.exactObject(value, path, ["state", "value", "unit"]);
    return {
      state: "known",
      value: context.finiteNumber(object.value, fieldPath(path, "value"), true),
      unit: context.enum(object.unit, fieldPath(path, "unit"), NUMERIC_UNITS),
    };
  }
  if (discriminator === "unknown") {
    context.exactObject(value, path, ["state"]);
    return { state: "unknown" };
  }
  if (discriminator === "unavailable") {
    const object = context.exactObject(value, path, ["state", "reason"]);
    return {
      state: "unavailable",
      reason: parseUnavailableReason(
        object.reason,
        fieldPath(path, "reason"),
        context,
      ),
    };
  }
  context.exactObject(value, path, ["state"]);
  context.issue(
    fieldPath(path, "state"),
    "unsupportedValue",
    "expected known, unknown, or unavailable",
  );
  return { state: "unknown" };
}
